import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Drawer } from "@/components/ui/Drawer";
import { Icon } from "@/components/ui/Icon";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { SegmentedFilter, Toolbar } from "@/components/ui/Toolbar";
import { ErrorState } from "@/components/ui/States";
import { request } from "@/lib/api/client";
import { downloadFile } from "@/lib/api/files";
import { usePeople } from "@/lib/api/people";
import { useAction, useItem, useList } from "@/lib/api/queries";
import { PERM } from "@/lib/auth/rbac";
import { useSession } from "@/lib/auth/useSession";
import { dateTime, qty, relative } from "@/lib/format";

/**
 * Erkin qabul / vozvrat sessiyalari (buyurtmachi qarori, 2026-10-31).
 *
 * Xodim TSD dan sessiyani o'zi boshlaydi (topshiriq va 1C buyurtmasisiz),
 * tovarni palletlarga sanaydi va har palletni katakka bog'laydi. Sessiya
 * yopilgach shu yerda:
 *   * Excel hisobot (palletlar, noma'lum kodlar, brak, xronologiya);
 *   * xodim 1C da qo'lda yaratgan nakladnoy RAQAMI biriktiriladi — WMS
 *     hujjatni 1C dan O'QIYDI va sanalgani bilan solishtiradi;
 *   * shundan keyin palletlar joylashtirishga ketadi: prixod — kara,
 *     vozvrat — vozvrat xodimining o'ziga OX ga to'ldirish.
 *
 * Noma'lum shtrix-kodli pallet kod nomenklaturada paydo bo'lguncha,
 * bojxona hududidagisi ruxsatgacha ushlab turiladi.
 */

type Kind = "RECEIPT" | "RETURN";

interface SessionLine {
  id: string;
  sku_id?: string;
  barcode?: string;
  qty: string;
  unknown: boolean;
  added_by: string;
  added_at: string;
  posted: boolean;
  quality_status?: string;
  target_location_code?: string;
  placed_at?: string;
}

interface SessionPallet {
  id: string;
  lp_code: string;
  status: string;
  location_code?: string;
  location_function?: string;
  release_kind?: string;
  release_seq?: number;
  stock_dirty?: boolean;
  held_reason?: string;
  lines: SessionLine[];
}

interface SessionDefect {
  id: string;
  sku_id?: string;
  barcode?: string;
  qty: string;
  reason: string;
  posted: boolean;
  recorded_by: string;
  recorded_at: string;
}

interface DiffRow {
  sku_id?: string;
  external_id?: string;
  invoice_qty: string;
  good_qty: string;
  defect_qty: string;
  received_qty: string;
  delta: string;
  status: string;
}

interface TimelineEvent {
  id: number;
  occurred_at: string;
  actor_id?: string;
  action: string;
  lp_code?: string;
  sku_id?: string;
  barcode?: string;
  qty?: string;
  location_code?: string;
  note?: string;
}

interface Summary {
  pallets: number;
  open_pallets: number;
  lines: number;
  unknown_lines: number;
  defects: number;
  held_pallets: number;
  released_pallets: number;
  customs_pallets: number;
  diff_issues: number;
  customs_hold: boolean;
}

interface ReceivingSession {
  id: string;
  kind: Kind;
  session_number: string;
  warehouse_code: string;
  target_zone: string;
  /** TSD yoki EXCEL (panel importi). */
  source?: string;
  source_file?: string;
  status: string;
  started_by: string;
  started_at: string;
  workers: string[];
  closed_at?: string;
  invoice_number?: string;
  invoice_date?: string;
  invoice_checked: boolean;
  invoice_attached_at?: string;
  customs_cleared_at?: string;
  customs_declaration?: string;
  summary: Summary;
  pallets?: SessionPallet[];
  defects?: SessionDefect[];
  unknown_barcodes?: Array<{ barcode: string; qty: string; defect_qty: string; lp_codes: string[] }>;
  diff?: DiffRow[];
  timeline?: TimelineEvent[];
}

interface Sku {
  id: string;
  article: string;
  name: string;
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: "sanalmoqda",
  CLOSED: "nakladnoy kutilmoqda",
  INVOICE_ATTACHED: "nakladnoy biriktirildi",
  CANCELLED: "bekor qilindi",
};

const STATUS_TONE: Record<string, "info" | "warning" | "success" | "neutral"> = {
  OPEN: "info",
  CLOSED: "warning",
  INVOICE_ATTACHED: "success",
  CANCELLED: "neutral",
};

const DIFF_LABEL: Record<string, string> = {
  MATCH: "mos",
  SHORT: "kam",
  OVER: "ortiqcha",
  MISSING: "kelmagan",
  EXTRA: "nakladnoyda yo'q",
  UNMAPPED: "1C tovari bog'lanmagan",
};

const DIFF_TONE: Record<string, "success" | "warning" | "error" | "neutral"> = {
  MATCH: "success",
  SHORT: "error",
  OVER: "warning",
  MISSING: "error",
  EXTRA: "warning",
  UNMAPPED: "neutral",
};

const HELD_LABEL: Record<string, string> = {
  UNKNOWN_BARCODE: "noma'lum kod",
  NOT_POSTED: "qoldiqqa yozilmagan",
  CUSTOMS_HOLD: "bojxona ruxsati kutilmoqda",
  EMPTY: "bo'sh",
};

const ACTION_LABEL: Record<string, string> = {
  STARTED: "Sessiya boshlandi",
  PALLET_OPENED: "Pallet ochildi",
  LINE_ADDED: "Tovar qo'shildi",
  LINE_REMOVED: "Satr o'chirildi",
  DEFECT_RECORDED: "Brak yozildi",
  PALLET_CLOSED: "Pallet yopildi (sessiya bilan)",
  PALLET_LOCATION_SET: "Pallet katagi",
  LINE_UPDATED: "Satr tahrirlandi",
  LINE_MOVED: "Satr boshqa palletga",
  STOCK_CORRECTED: "Qoldiq tuzatildi",
  RELEASE_CANCELLED: "Joylashtirish bekor (tahrir)",
  PALLET_DROPPED: "Bo'sh pallet chiqarildi",
  CLOSED: "Sessiya yopildi",
  INVOICE_ATTACHED: "1C nakladnoyi biriktirildi",
  CUSTOMS_CLEARED: "Bojxona ruxsati",
  UNKNOWN_RESOLVED: "Noma'lum kod bog'landi",
  RELEASED: "Joylashtirishga uzatildi",
  PLACED: "OX ga joylandi",
  CANCELLED: "Bekor qilindi",
};

type Tab = "awaiting" | "open" | "all";

export function ReceivingSessions({ kind }: { kind: Kind }) {
  const isReturn = kind === "RETURN";
  const [tab, setTab] = useState<Tab>("awaiting");
  const [search, setSearch] = useState("");
  const [openedId, setOpenedId] = useState<string | null>(null);

  const status = tab === "awaiting" ? "CLOSED" : tab === "open" ? "OPEN" : undefined;
  const list = useList<ReceivingSession>(
    "inbound",
    "/api/v1/receiving-sessions",
    { kind, status, limit: 200 },
    { refetchInterval: 15_000 },
  );
  const people = usePeople((list.data?.items ?? []).map((row) => row.started_by));

  const needle = search.trim().toLowerCase();
  const rows = (list.data?.items ?? []).filter(
    (row) =>
      !needle ||
      [row.session_number, row.invoice_number, people.get(row.started_by)].some((value) =>
        value?.toLowerCase().includes(needle),
      ),
  );

  const columns: Array<Column<ReceivingSession>> = [
    { key: "number", header: "Sessiya", render: (row) => <span className="mono">{row.session_number}</span> },
    {
      key: "status",
      header: "Holat",
      render: (row) => (
        <StatusBadge value={STATUS_LABEL[row.status] ?? row.status} tone={STATUS_TONE[row.status] ?? "info"} />
      ),
    },
    { key: "worker", header: "Xodim", render: (row) => people.get(row.started_by) ?? "—" },
    { key: "started", header: "Boshlangan", render: (row) => relative(row.started_at) },
    { key: "pallets", header: "Pallet", align: "right", render: (row) => row.summary.pallets },
    { key: "lines", header: "Satr", align: "right", render: (row) => row.summary.lines },
    {
      key: "issues",
      header: "Diqqat",
      render: (row) => (
        <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
          {row.summary.unknown_lines > 0 && (
            <StatusBadge value={`noma'lum: ${row.summary.unknown_lines}`} tone="warning" />
          )}
          {row.summary.defects > 0 && <StatusBadge value={`brak: ${row.summary.defects}`} tone="error" />}
          {row.summary.customs_hold && <StatusBadge value="bojxona" tone="warning" />}
          {row.summary.diff_issues > 0 && <StatusBadge value={`farq: ${row.summary.diff_issues}`} tone="error" />}
        </span>
      ),
    },
    {
      key: "invoice",
      header: "1C nakladnoy",
      render: (row) => (row.invoice_number ? <span className="mono">{row.invoice_number}</span> : "—"),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <span style={{ display: "inline-flex", gap: 6, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="btn btn-text btn-sm"
            title="Excel hisobot"
            onClick={() =>
              void downloadFile("inbound", `/api/v1/receiving-sessions/${row.id}/report.xlsx`, `${row.session_number}.xlsx`)
            }
          >
            <Icon name="download" size={14} />
            Excel
          </button>
          <button type="button" className="btn btn-tonal btn-sm" onClick={() => setOpenedId(row.id)}>
            <Icon name="search" size={14} />
            Ochish
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={isReturn ? "Vozvrat sessiyalari" : "Qabul sessiyalari"}
        hint={
          isReturn
            ? "TSD da topshiriqsiz qabul qilingan vozvratlar. 1C vozvrat nakladnoyi raqamini biriktiring — WMS uni o'qib solishtiradi va tovar vozvrat xodimining o'ziga OX ga joylash topshirig'i bo'ladi."
            : "TSD da topshiriqsiz qabul qilingan prixodlar. 1C prixod nakladnoyi raqamini biriktiring — WMS uni o'qib solishtiradi va palletlar karachilarga joylashtirishga ketadi."
        }
      />
      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Sessiya, nakladnoy yoki xodim…"
        onRefresh={() => void list.refetch()}
        refreshing={list.isFetching}
      >
        <SegmentedFilter
          value={tab}
          onChange={setTab}
          options={[
            { value: "awaiting", label: "Nakladnoy kutilmoqda" },
            { value: "open", label: "Sanalmoqda" },
            { value: "all", label: "Hammasi" },
          ]}
        />
      </Toolbar>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        onRowClick={(row) => setOpenedId(row.id)}
        activeKey={openedId ?? undefined}
        emptyTitle="Sessiya yo'q"
        emptyHint="Xodim TSD dagi «Qabul va vozvrat» bo'limidan sessiyani boshlaydi."
        hasMore={list.data?.has_more}
        footer={<span>{rows.length} ta sessiya</span>}
      />
      {openedId && <SessionDrawer id={openedId} onClose={() => setOpenedId(null)} />}
    </>
  );
}

function SessionDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const session = useSession();
  const canAttach = session.can(PERM.inboundApprove) || session.can(PERM.inboundArrive);
  const detail = useItem<ReceivingSession>("inbound", `/api/v1/receiving-sessions/${id}`);
  const data = detail.data;
  const [invoice, setInvoice] = useState("");
  const [declaration, setDeclaration] = useState("");
  const [edit, setEdit] = useState<EditTarget | null>(null);
  // Yopilgandan keyingi tahrir (buyurtmachi, 2026-11-01): pallet hali
  // qabul/bojxona/VZ katagida va joylashtirish boshlanmagan bo'lsa.
  // Excel sessiyasi (panelda yuklangan) ochiqligida ham panel tahrirlaydi
  // va yopadi.
  const excelOpen = data?.source === "EXCEL" && data?.status === "OPEN";
  const canEdit = canAttach && (data?.status === "CLOSED" || data?.status === "INVOICE_ATTACHED" || excelOpen);
  const [closing, setClosing] = useState<"close" | "cancel" | null>(null);
  const closeSession = useAction<ReceivingSession, string>("inbound", () => ({
    path: `/api/v1/receiving-sessions/${id}/close`,
    method: "POST",
  }));
  const cancelSession = useAction<ReceivingSession, string>("inbound", (reason) => ({
    path: `/api/v1/receiving-sessions/${id}/cancel`,
    method: "POST",
    body: { reason },
  }));

  const attach = useAction<ReceivingSession, string>("inbound", (number) => ({
    path: `/api/v1/receiving-sessions/${id}/invoice`,
    method: "POST",
    body: { invoice_number: number.trim() },
  }));
  const clear = useAction<ReceivingSession, string>("inbound", (number) => ({
    path: `/api/v1/receiving-sessions/${id}/customs-clearance`,
    method: "POST",
    body: { declaration_number: number.trim() },
  }));
  const resolve = useAction<ReceivingSession>("inbound", () => ({
    path: `/api/v1/receiving-sessions/${id}/resolve-unknown`,
    method: "POST",
  }));

  const skuIds = useMemo(() => {
    const ids = new Set<string>();
    for (const pallet of data?.pallets ?? []) for (const line of pallet.lines) if (line.sku_id) ids.add(line.sku_id);
    for (const defect of data?.defects ?? []) if (defect.sku_id) ids.add(defect.sku_id);
    for (const row of data?.diff ?? []) if (row.sku_id) ids.add(row.sku_id);
    return [...ids].sort();
  }, [data]);
  const skus = useList<Sku>("masterData", "/api/v1/skus", { ids: skuIds.join(","), limit: 500 }, {
    enabled: skuIds.length > 0,
  });
  const skuMap = useMemo(() => new Map((skus.data?.items ?? []).map((sku) => [sku.id, sku] as const)), [skus.data]);
  const people = usePeople([
    ...(data?.workers ?? []),
    ...(data?.timeline ?? []).map((event) => event.actor_id),
  ]);
  const skuLabel = (skuId?: string, barcode?: string) => {
    if (!skuId) return <span className="mono">noma'lum: {barcode}</span>;
    const sku = skuMap.get(skuId);
    return sku ? (
      <>
        <span className="mono">{sku.article}</span> {sku.name}
      </>
    ) : (
      <span className="mono">{barcode || skuId.slice(0, 8)}</span>
    );
  };

  return (
    <Drawer label="Qabul sessiyasi">
      <div className="drawer-head">
        <div>
          <div className="drawer-title mono">{data?.session_number ?? "…"}</div>
          {data && (
            <div className="drawer-sub">
              {data.kind === "RETURN" ? "Vozvrat" : "Prixod"} · {STATUS_LABEL[data.status] ?? data.status} ·{" "}
              {dateTime(data.started_at)} · {(data.workers ?? []).map((worker) => people.get(worker) ?? "…").join(", ")}
              {data.source === "EXCEL" ? ` · Excel: ${data.source_file ?? ""}` : ""}
            </div>
          )}
        </div>
        <span style={{ display: "inline-flex", gap: 6 }}>
          {data && (
            <button
              type="button"
              className="btn btn-tonal btn-sm"
              onClick={() =>
                void downloadFile("inbound", `/api/v1/receiving-sessions/${id}/report.xlsx`, `${data.session_number}.xlsx`)
              }
            >
              <Icon name="download" size={14} />
              Excel
            </button>
          )}
          <button type="button" className="btn btn-text btn-sm" onClick={onClose} aria-label="Yopish">
            <Icon name="close" size={16} />
          </button>
        </span>
      </div>

      <div className="drawer-body">
        {detail.isLoading && <div className="skeleton" style={{ height: 200 }} />}
        {detail.error && <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />}
        {data && (
          <>
            {canAttach && excelOpen && (
              <section>
                <h3 className="section-title">Excel sessiyasi</h3>
                <p className="field-hint">
                  Fayldan yaratilgan, hali ochiq. Tekshirib (kerak bo'lsa tahrirlab) yoping — qoldiq shu paytda
                  yoziladi.
                </p>
                <span style={{ display: "inline-flex", gap: 8 }}>
                  <button type="button" className="btn btn-filled btn-sm" onClick={() => setClosing("close")}>
                    <Icon name="check" size={14} />
                    Sessiyani yopish
                  </button>
                  <button type="button" className="btn btn-outlined btn-sm" onClick={() => setClosing("cancel")}>
                    Bekor qilish
                  </button>
                </span>
              </section>
            )}

            <section>
              <h3 className="section-title">1C nakladnoyi</h3>
              {data.invoice_number ? (
                <p className="field-hint">
                  <b className="mono">{data.invoice_number}</b>
                  {data.invoice_date ? <> · {data.invoice_date}</> : null} · biriktirildi{" "}
                  {relative(data.invoice_attached_at)} ·{" "}
                  {data.invoice_checked ? "1C dan o'qildi va solishtirildi" : "1C o'qish kanali sozlanmagan — solishtirilmadi"}
                </p>
              ) : (
                <p className="field-hint">
                  {data.status === "OPEN"
                    ? "Sessiya hali yopilmagan — xodim TSD da sanayapti."
                    : "1C da nakladnoyni qo'lda yarating va raqamini kiriting. Palletlar shundan keyin joylashtirishga ketadi."}
                </p>
              )}
              {canAttach && (data.status === "CLOSED" || data.status === "INVOICE_ATTACHED") && (
                <form
                  style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (invoice.trim()) attach.mutate(invoice, { onSuccess: () => setInvoice("") });
                  }}
                >
                  <label className="field" style={{ flex: 1 }}>
                    <span className="field-label">
                      {data.invoice_number ? "Boshqa raqam (qayta o'qish)" : "1C nakladnoy raqami"}
                    </span>
                    <input
                      className="input mono"
                      value={invoice}
                      placeholder="00GD-000123"
                      onChange={(event) => setInvoice(event.target.value)}
                    />
                  </label>
                  <button type="submit" className="btn btn-filled" disabled={attach.isPending || !invoice.trim()}>
                    <Icon name="check" size={16} />
                    Biriktirish
                  </button>
                </form>
              )}
              {attach.error && <ErrorState error={attach.error} />}
            </section>

            {(data.diff ?? []).length > 0 && (
              <section>
                <h3 className="section-title">Solishtirish: nakladnoy va fakt</h3>
                <div className="table-scroll">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Tovar</th>
                        <th className="right">Nakladnoyda</th>
                        <th className="right">Yaroqli</th>
                        <th className="right">Brak</th>
                        <th className="right">Farq</th>
                        <th>Natija</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.diff ?? []).map((row, index) => (
                        <tr key={`${row.sku_id ?? row.external_id}-${index}`}>
                          <td>{row.sku_id ? skuLabel(row.sku_id) : <span className="mono">{row.external_id}</span>}</td>
                          <td className="right num">{qty(row.invoice_qty)}</td>
                          <td className="right num">{qty(row.good_qty)}</td>
                          <td className="right num">{qty(row.defect_qty)}</td>
                          <td className="right num">{qty(row.delta)}</td>
                          <td>
                            <StatusBadge value={DIFF_LABEL[row.status] ?? row.status} tone={DIFF_TONE[row.status] ?? "neutral"} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {data.kind === "RECEIPT" && data.summary.customs_pallets > 0 && (
              <section>
                <h3 className="section-title">Bojxona</h3>
                {data.customs_cleared_at ? (
                  <p className="field-hint">
                    Ruxsat {dateTime(data.customs_cleared_at)}
                    {data.customs_declaration ? <> · {data.customs_declaration}</> : null}
                  </p>
                ) : (
                  <>
                    <p className="field-hint">
                      {data.summary.customs_pallets} ta pallet bojxona hududida — qoldiq ruxsatgacha bloklangan va
                      joylashtirilmaydi.
                    </p>
                    {canAttach && data.status !== "OPEN" && (
                      <form
                        style={{ display: "flex", gap: 8, alignItems: "flex-end" }}
                        onSubmit={(event) => {
                          event.preventDefault();
                          clear.mutate(declaration);
                        }}
                      >
                        <label className="field" style={{ flex: 1 }}>
                          <span className="field-label">Deklaratsiya raqami</span>
                          <input className="input mono" value={declaration} onChange={(e) => setDeclaration(e.target.value)} />
                        </label>
                        <button type="submit" className="btn btn-outlined" disabled={clear.isPending}>
                          Ruxsat berildi
                        </button>
                      </form>
                    )}
                    {clear.error && <ErrorState error={clear.error} />}
                  </>
                )}
              </section>
            )}

            {(data.unknown_barcodes ?? []).length > 0 && (
              <section>
                <h3 className="section-title">Noma'lum shtrix-kodlar</h3>
                <p className="field-hint">
                  Kod nomenklaturada (1C sinxronida) paydo bo'lganda o'zi bog'lanadi. Shungacha palleti
                  joylashtirilmaydi.
                </p>
                <table className="data">
                  <thead>
                    <tr>
                      <th>Shtrix-kod</th>
                      <th className="right">Miqdor</th>
                      <th className="right">Brak</th>
                      <th>Palletlar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.unknown_barcodes ?? []).map((row) => (
                      <tr key={row.barcode}>
                        <td className="mono">{row.barcode}</td>
                        <td className="right num">{qty(row.qty)}</td>
                        <td className="right num">{qty(row.defect_qty)}</td>
                        <td className="mono">{row.lp_codes.join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  className="btn btn-outlined btn-sm"
                  disabled={resolve.isPending}
                  onClick={() => resolve.mutate()}
                >
                  <Icon name="refresh" size={14} />
                  Nomenklaturadan qayta tekshirish
                </button>
                {resolve.error && <ErrorState error={resolve.error} />}
              </section>
            )}

            <section>
              <h3 className="section-title">Palletlar ({data.summary.pallets})</h3>
              {(data.pallets ?? []).map((pallet) => (
                <div key={pallet.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <b className="mono">{pallet.lp_code}</b>
                    <span className="muted">
                      {pallet.status === "OPEN" ? "ochiq" : `katak ${pallet.location_code ?? "—"}`}
                      {pallet.location_function === "CUSTOMS" ? " · bojxona" : ""}
                    </span>
                    {pallet.release_kind && (
                      <StatusBadge
                        value={pallet.release_kind === "PLACEMENT" ? "OX ga joylashga" : "karaga uzatildi"}
                        tone="success"
                      />
                    )}
                    {pallet.held_reason && (
                      <StatusBadge value={HELD_LABEL[pallet.held_reason] ?? pallet.held_reason} tone="warning" />
                    )}
                    {pallet.stock_dirty && <StatusBadge value="qoldiq tuzatilmoqda" tone="warning" />}
                    {(pallet.release_seq ?? 0) > 1 && (
                      <span className="muted">qayta uzatildi ×{pallet.release_seq}</span>
                    )}
                    {canEdit && !pallet.lines.some((line) => line.placed_at) && (
                      <button
                        type="button"
                        className="btn btn-text btn-sm"
                        onClick={() => setEdit({ action: "cell", pallet })}
                      >
                        <Icon name="map" size={14} />
                        Katak
                      </button>
                    )}
                  </div>
                  <table className="data">
                    <tbody>
                      {pallet.lines.map((line) => (
                        <tr key={line.id}>
                          <td>{skuLabel(line.sku_id, line.barcode)}</td>
                          <td className="mono">{line.barcode}</td>
                          <td className="right num">{qty(line.qty)}</td>
                          <td className="muted">
                            {line.quality_status === "CUSTOMS_HOLD" ? "bojxona" : ""}
                            {line.target_location_code ? ` OX ${line.target_location_code}` : ""}
                            {line.placed_at ? " · joylandi" : ""}
                          </td>
                          {canEdit && (
                            <td className="right" style={{ whiteSpace: "nowrap" }}>
                              {!line.placed_at && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-text btn-sm"
                                    onClick={() => setEdit({ action: "qty", pallet, line })}
                                  >
                                    Miqdor
                                  </button>
                                  {line.unknown && (
                                    <button
                                      type="button"
                                      className="btn btn-text btn-sm"
                                      onClick={() => setEdit({ action: "sku", pallet, line })}
                                    >
                                      Tovar
                                    </button>
                                  )}
                                  {(data.pallets ?? []).length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-text btn-sm"
                                      onClick={() => setEdit({ action: "move", pallet, line })}
                                    >
                                      Ko'chirish
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="btn btn-text btn-sm"
                                    onClick={() => setEdit({ action: "remove", pallet, line })}
                                  >
                                    O'chirish
                                  </button>
                                </>
                              )}
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </section>

            {(data.defects ?? []).length > 0 && (
              <section>
                <h3 className="section-title">Brak ({data.summary.defects})</h3>
                <table className="data">
                  <tbody>
                    {(data.defects ?? []).map((defect) => (
                      <tr key={defect.id}>
                        <td>{skuLabel(defect.sku_id, defect.barcode)}</td>
                        <td className="right num">{qty(defect.qty)}</td>
                        <td>{defect.reason}</td>
                        <td className="muted">{defect.posted ? "BRAK katagida" : "qayd"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            <section>
              <h3 className="section-title">Jarayon</h3>
              <table className="data">
                <tbody>
                  {(data.timeline ?? []).map((event) => (
                    <tr key={event.id}>
                      <td className="muted">{dateTime(event.occurred_at)}</td>
                      <td>{event.actor_id ? people.get(event.actor_id) ?? "…" : "tizim"}</td>
                      <td>{ACTION_LABEL[event.action] ?? event.action}</td>
                      <td className="mono">
                        {[event.lp_code, event.barcode, event.qty && qty(event.qty), event.location_code]
                          .filter(Boolean)
                          .join(" · ")}
                      </td>
                      <td className="muted">{event.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
      <ConfirmDialog
        open={closing !== null}
        title={closing === "cancel" ? "Excel sessiyasini bekor qilish" : "Sessiyani yopish"}
        description={
          closing === "cancel"
            ? "Sessiya bekor qilinadi, LP lar bo'shaydi. Faylni tuzatib qayta yuklash mumkin."
            : "Har tovarli palletning katagi bo'lishi shart. Qoldiq yoziladi, keyin 1C nakladnoyini biriktiring."
        }
        confirmLabel={closing === "cancel" ? "Bekor qilish" : "Yopish"}
        danger={closing === "cancel"}
        requireReason={closing === "cancel"}
        busy={closeSession.isPending || cancelSession.isPending}
        error={closing === "cancel" ? cancelSession.error : closeSession.error}
        onClose={() => setClosing(null)}
        onConfirm={(reason) => {
          const action = closing === "cancel" ? cancelSession : closeSession;
          action.mutate(reason, { onSuccess: () => setClosing(null) });
        }}
      />
      {edit && data && (
        <SessionEditDialog
          sessionId={id}
          kind={data.kind}
          target={edit}
          pallets={data.pallets ?? []}
          onClose={() => setEdit(null)}
        />
      )}
    </Drawer>
  );
}

type EditTarget =
  | { action: "cell"; pallet: SessionPallet }
  | { action: "qty" | "sku" | "move" | "remove"; pallet: SessionPallet; line: SessionLine };

const EDIT_TITLE: Record<EditTarget["action"], string> = {
  cell: "Pallet katagini o'zgartirish",
  qty: "Miqdorni tuzatish",
  sku: "Noma'lum satrga tovar",
  move: "Satrni boshqa palletga ko'chirish",
  remove: "Satrni o'chirish",
};

/**
 * Yopilgan sessiyani tahrirlash dialogi. Har amal sabab bilan
 * yuboriladi; server qoldiqni tuzatadi (kompensatsiya, idempotent),
 * boshlanmagan joylashtirishni bekor qilib qaytadan ochadi va 1C
 * solishtiruvini qayta hisoblaydi. Pallet joylashtirilayotgan bo'lsa
 * server rad etadi — xato shu dialogda ko'rinadi.
 */
function SessionEditDialog({
  sessionId,
  kind,
  target,
  pallets,
  onClose,
}: {
  sessionId: string;
  kind: Kind;
  target: EditTarget;
  pallets: SessionPallet[];
  onClose: () => void;
}) {
  const client = useQueryClient();
  const line = target.action === "cell" ? undefined : target.line;
  const [value, setValue] = useState(() =>
    target.action === "qty" && line ? line.qty : target.action === "cell" ? target.pallet.location_code ?? "" : "",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const others = pallets.filter((pallet) => pallet.id !== target.pallet.id);

  async function submit(reason: string) {
    setBusy(true);
    setError(null);
    const base = `/api/v1/receiving-sessions/${sessionId}`;
    try {
      if (target.action === "cell") {
        const code = value.trim().toUpperCase();
        const location = await request<{ id: string }>("topology", `/api/v1/locations/by-code/${encodeURIComponent(code)}`);
        await request("inbound", `${base}/pallets/${target.pallet.id}/location`, {
          method: "POST",
          body: { location_id: location.id, reason },
        });
      } else if (target.action === "remove") {
        await request("inbound", `${base}/lines/${target.line.id}/remove`, { method: "POST", body: { reason } });
      } else {
        const body: Record<string, string> = { reason };
        if (target.action === "qty") body.qty = value.trim();
        if (target.action === "move") body.pallet_id = value;
        if (target.action === "sku") {
          const code = value.trim();
          const sku = await request<{ id: string }>("masterData", `/api/v1/skus/by-barcode/${encodeURIComponent(code)}`);
          body.sku_id = sku.id;
        }
        await request("inbound", `${base}/lines/${target.line.id}/update`, { method: "POST", body });
      }
      void client.invalidateQueries({ queryKey: ["inbound"] });
      onClose();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  const label =
    target.action === "cell"
      ? kind === "RETURN"
        ? "VZ katak kodi"
        : "Qabul yoki bojxona katagi kodi"
      : target.action === "qty"
        ? "Yangi miqdor"
        : target.action === "sku"
          ? "Tovarning shtrix-kodi (nomenklaturada bor)"
          : "Qaysi palletga";

  return (
    <ConfirmDialog
      open
      title={EDIT_TITLE[target.action]}
      description={
        <>
          Pallet <b className="mono">{target.pallet.lp_code}</b>
          {line ? (
            <>
              {" "}
              · <span className="mono">{line.barcode || line.sku_id?.slice(0, 8)}</span> · {qty(line.qty)}
            </>
          ) : null}
          . Qoldiq tuzatiladi; joylashtirish topshirig'i boshlanmagan bo'lsa bekor qilinib qayta ochiladi. Bir xil
          tovar shu palletda bo'lsa miqdorlar qo'shiladi.
        </>
      }
      confirmLabel={target.action === "remove" ? "O'chirish" : "Saqlash"}
      danger={target.action === "remove"}
      requireReason
      reasonPlaceholder="Nega tuzatilmoqda?"
      busy={busy}
      error={error}
      confirmDisabled={target.action !== "remove" && !value.trim()}
      extra={
        target.action === "remove" ? undefined : (
          <label className="field">
            <span className="field-label">{label}</span>
            {target.action === "move" ? (
              <select className="input" value={value} onChange={(event) => setValue(event.target.value)}>
                <option value="">—</option>
                {others.map((pallet) => (
                  <option key={pallet.id} value={pallet.id}>
                    {pallet.lp_code}
                    {pallet.location_code ? ` · ${pallet.location_code}` : ""}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="input mono"
                value={value}
                inputMode={target.action === "qty" ? "decimal" : undefined}
                onChange={(event) => setValue(event.target.value)}
              />
            )}
          </label>
        )
      }
      onConfirm={(reason) => void submit(reason)}
      onClose={onClose}
    />
  );
}
